import GubiLogo from '@components/icons/GubiAnimatedLogo/GubiAnimatedLogo'
import ShoppingCartIcon from '@components/icons/ShoppingCartIcon/ShoppingCartIcon'
import Cross from '@components/ui/Icons/Cross/Cross'
import Hamburger from '@components/ui/Icons/Hamburger/Hamburger'
import Heart from '@components/ui/Icons/Heart/Heart'
import Location from '@components/ui/Icons/Location/Location'
import Search from '@components/ui/Icons/Search/Search'
import {
  fixedClearAllBodyScrollLocks,
  fixedDisableBodyScroll,
} from '@lib/fixedScrollHelpers'
import useMediaQuery from '@lib/hooks/useMediaQuery'
import useNavigator from '@lib/hooks/useNavigator'
import {
  selectCalloutBanner,
  selectCountry,
  selectEcommerceEnabled,
  selectHeaderData,
  selectRetailerUrl,
  selectWishlistUrl,
} from '@redux/globalSlice'
import { selectWishlistCount } from '@redux/wishlistSlice'

import Account from '@components/ui/Icons/Account/Account'
import { useCart } from '@features/cart/api/get-cart'
import { setOnFlyoutWillClose, setOnFlyoutWillOpen } from '@redux/navSlice'
import { isTouch } from '@utils/helpers/DeviceUtils'
import clsx from 'clsx'
import { throttle } from 'lodash-es'
import { useRouter } from 'next/router'
import { FC, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import invariant from 'tiny-invariant'
import Link from '../Link/Link'
import CalloutBanner from './CalloutBanner/CalloutBanner'
import Cart from './Cart/Cart'
import CartPreview from './CartPreview/CartPreview'
import NavItem from './NavItem'
import s from './Navbar.module.scss'

export type NavbarProps = {
  headerSpace: boolean
}

type NavFlyout = {
  item?: ParentNode
  flyout?: HTMLElement
  scrollContainer?: HTMLElement
  content?: HTMLElement
}
type Submenu = {
  content?: HTMLElement
  groups: HTMLElement
}

const Navbar: FC<NavbarProps> = ({ headerSpace }) => {
  const isBreakpointWithMenu = useMediaQuery('(max-width: 1280px)')
  const backgroundRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const navigate = useNavigator()
  const dispatch = useDispatch()
  const headerSpaceRef = useRef<HTMLDivElement>(null)
  const rootWrapper = useRef<HTMLDivElement>(null)
  const calloutRoot = useRef<HTMLElement>(null)
  const flyouts = useRef<NavFlyout[]>([])
  const subMenus = useRef<Submenu[]>([])
  const subMenuEls = useRef<HTMLElement[]>([])
  const navRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const searchFlyout = useRef<HTMLLIElement>(null)
  const menuList = useRef<HTMLDivElement>(null)
  const shouldDelaySearchOpen = useRef<boolean>(false)
  const flyoutAbortController = useRef<AbortController>(null)
  const menuButton = useRef<HTMLButtonElement>(null)
  const cartPreview = useRef<HTMLFormElement>(null)
  const searchPreview = useRef<HTMLDivElement>(null)
  const flyoutSearchInput = useRef<HTMLInputElement>(null)
  const cartFlyout = useRef<HTMLLIElement>(null)
  const headerDataV2 = useSelector(selectHeaderData)

  const cartQuery = useCart()
  const totalQuantity = cartQuery.data?.lineItemsQuantity
  const ecommerceEnabled = useSelector(selectEcommerceEnabled)
  const country = useSelector(selectCountry)
  const retailerUrl = useSelector(selectRetailerUrl)
  const wishlistUrl = useSelector(selectWishlistUrl)
  const flyoutAbortControllerSignal = useRef(null)
  const menuFlyout = useRef<HTMLLIElement>(null)
  const currentSubmenuMouseOverTimeout = useRef<NodeJS.Timeout>(null)
  const currentFlyout = useRef<HTMLElement>(null)
  const calloutBanner = useSelector(selectCalloutBanner)
  const wishlistCount = useSelector(selectWishlistCount)
  const disableCartPreview = useMemo(() => {
    return (
      router.pathname.endsWith('/cart') || router.pathname.endsWith('/checkout')
    )
  }, [router.pathname])

  const useSpecialLogo = useMemo(() => {
    if (
      router.asPath.includes('/products/gubi-for-unicef-pacha-lounge-chair') ||
      router.asPath.includes('/stories/gubi-for-unicef')
    )
      return true

    return false
  }, [router.pathname])

  useEffect(() => {
    const handleScroll = throttle(() => {
      const offset = 0
      const { scrollTop } = document.documentElement
      const scrolled = scrollTop > offset
      if (scrolled) {
        navRef.current.classList.remove(s.transparent)
        backgroundRef.current.classList.add('shadow-magical')
      } else {
        !headerSpace && navRef.current.classList.add(s.transparent)
        backgroundRef.current.classList.remove('shadow-magical')
      }
    }, 200)

    handleScroll()

    document.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [headerSpace, navRef.current, backgroundRef.current])

  const onMenuItemMouseOver = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    const target = e.target as HTMLElement
    if (isTouch()) return
    const t = target.closest(`.${s.item}`) as HTMLElement
    clearTimeout(currentSubmenuMouseOverTimeout.current)
    currentSubmenuMouseOverTimeout.current = setTimeout(() => {
      if (t && t !== currentFlyout.current && t !== menuFlyout.current) {
        if (subMenuEls.current.includes(t)) {
          openFlyout(t)
        } else {
          closeFlyout(currentFlyout.current)
        }
      }
    }, 120)
  }

  const onMenuItemMouseLeave = () => {
    clearTimeout(currentSubmenuMouseOverTimeout.current)
    closeFlyout(currentFlyout.current)
  }

  const onFlyoutWillOpen = (e: HTMLElement, t?) => {
    if (isBreakpointWithMenu) {
      fixedDisableBodyScroll(contentRef.current)
    }
    dispatch(setOnFlyoutWillOpen())

    !subMenuEls.current.includes(e) ||
      navRef.current.classList.add(s.withSubmenuOpen)

    navRef.current?.addEventListener(
      'mouseleave',
      () => {
        !isBreakpointWithMenu && onMenuItemMouseLeave()
      },
      { once: true },
    )
    currentFlyout.current = e
    flyoutAbortController.current = new AbortController()
    flyoutAbortControllerSignal.current = {
      signal: flyoutAbortController.current?.signal,
    }
    navRef.current?.classList.add(s.animating)

    if (t) {
      e.classList.add(s.itemFlyoutChangeNext)
    } else {
      e.classList.add(s.itemFlyoutOpen)
      navRef.current?.classList.add(s.withFlyoutOpen)
      navRef.current?.classList.remove(s.withCartPreviewOpen)
      navRef.current?.classList.remove(s.withSearchPreviewOpen)

      !isBreakpointWithMenu &&
        !isTouch() &&
        document.addEventListener(
          'scroll',
          () => closeFlyout(currentFlyout.current),
          { signal: flyoutAbortController.current?.signal },
        )
    }

    if (isBreakpointWithMenu) {
      setFlyoutHeight(e)
    }
  }

  const onMenuBackButtonClick = (e) => {
    openFlyout(menuFlyout.current)
  }
  const openFlyout = (e: HTMLElement) => {
    let type, el, onEnd
    if (currentFlyout.current) {
      const cFlyout = currentFlyout.current
      navRef.current.style.setProperty(
        '--next-flyout-height',
        getComputedStyle(getFlyout(e)).getPropertyValue('--flyout-height'),
      )
      navRef.current.style.setProperty(
        '--previous-flyout-height',
        getComputedStyle(getFlyout(cFlyout)).getPropertyValue(
          '--flyout-height',
        ),
      )
      onFlyoutWillClose(cFlyout, true)
      onFlyoutWillOpen(e, true)
      el = isBreakpointWithMenu ? contentRef.current : e
      type = 'animationend'
      onEnd = (e) => {
        onFlyoutOpen(e, true)
        onFlyoutClose(cFlyout, true)
      }
    } else {
      onFlyoutWillOpen(e)
      el = isBreakpointWithMenu ? contentRef.current : e
      type = 'transitionend'
      onEnd = (e) => {
        if (currentFlyout.current === searchFlyout.current) {
          flyoutSearchInput.current?.focus()
        }
        onFlyoutOpen(e)
      }
    }

    listenForAnimationChangeEnd(e, el, type, onEnd)
  }
  const getFlyout = (e) => {
    return getFlyoutItem(e).flyout
  }
  const getFlyoutItem = (e) => {
    return flyouts.current.find((t) => t.item === e)
  }
  const onFlyoutWillClose = (e: HTMLElement, t?) => {
    if (isBreakpointWithMenu) fixedClearAllBodyScrollLocks()
    dispatch(setOnFlyoutWillClose())
    navRef.current.classList.add(s.withFlyoutClosing, s.animating)
    e.classList.add(s.itemFlyoutClosing)
    e.classList.remove(s.itemFlyoutOpen)
    t
      ? e.classList.add(s.itemFlyoutChangePrevious)
      : navRef.current.classList.remove(s.withFlyoutOpen),
      flyoutAbortController.current && flyoutAbortController.current.abort()

    subMenuEls.current.includes(e) &&
      navRef.current.classList.remove(s.withSubmenuOpen)
    currentFlyout.current = null
    e === cartFlyout.current && navRef.current.classList.remove(s.withCartOpen)
    e === searchFlyout.current &&
      navRef.current.classList.remove(s.withSearchOpen)
  }
  const onFlyoutClose = (e: HTMLElement, t?) => {
    const navFlyout = flyouts.current.find((i) => i.item === e)
    navRef.current.classList.remove(s.animating, s.withFlyoutClosing)
    e.classList.remove(s.itemFlyoutClosing)
    if (t) {
      navFlyout.flyout.classList.remove(s.flyoutShort)
      e.classList.remove(s.itemFlyoutChangePrevious, s.itemFlyoutOpen)
    }
    if (shouldDelaySearchOpen.current) {
      shouldDelaySearchOpen.current && openSearch()
      shouldDelaySearchOpen.current = false
    }
  }
  const closeFlyout = (e = currentFlyout?.current) => {
    e !== currentFlyout.current ||
      !currentFlyout.current ||
      (onFlyoutWillClose(e),
      listenForAnimationChangeEnd(
        e,
        isBreakpointWithMenu ? contentRef.current : e,
        'transitionend',
        () => {
          onFlyoutClose(e)
        },
      ))
  }

  const onFlyoutOpen = (e: HTMLElement, t?) => {
    navRef.current.classList.remove(s.animating)
    if (t) e.classList.add(s.itemFlyoutOpen)
    e.classList.remove(s.itemFlyoutChangeNext)
  }
  const openMenu = () => {
    openFlyout(menuFlyout.current)
    navRef.current.classList.add(s.withMenuOpen)
  }

  const closeMenu = () => {
    navRef.current?.classList.remove(s.withMenuOpen)
    closeFlyout()
  }

  const toggleMenu = () => {
    navRef.current.classList.contains(s.withMenuOpen) ? closeMenu() : openMenu()
  }

  const listenForAnimationChangeEnd = (e, el, type, onEnd) => {
    const abortContoller = new AbortController(),
      signal = abortContoller.signal
    const flyout = flyouts.current.find((i) => i.item === e)

    const listener = ({ animationName, propertyName }) => {
      const listenerArr = [
        animationName === s.scrimHeightChange,
        animationName === s.flyoutSlideForwardNext,
        animationName === s.flyoutSlideBackNext,
        propertyName === 'height' && flyout,
        propertyName === 'height' && el === contentRef.current,
      ]

      if (listenerArr.some((x) => !!x)) {
        abortContoller.abort()
        onEnd(e)
      }
    }
    el.addEventListener(type, listener, {
      signal,
    }),
      el.addEventListener('transitioncancel', listener, {
        signal,
      }),
      el.addEventListener('animationcancel', listener, {
        signal,
      })
  }

  const onMenuButtonClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (navRef.current.classList.contains(s.withMenuOpen)) {
      closeMenu()
    } else if (navRef.current.classList.contains(s.withFlyoutOpen)) {
      // return
    } else {
      openMenu()
    }

    if (currentFlyout.current === searchFlyout.current) {
      closeSearch()
      return
    }
    if (currentFlyout.current === cartFlyout.current) {
      closeCart()
      return
    }
  }

  const onMenuItemButtonClick = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    const target = e.target as HTMLElement
    const q = !!target.closest(`.${s.submenuTriggerGroup}`)
    if (
      !!target.closest(`.${s.submenuTriggerButton}`) ||
      (q && isBreakpointWithMenu)
    ) {
      const itemSubmenu = target.closest(`.${s.itemSubmenu}`) as HTMLElement
      const open = isFlyoutOpen(itemSubmenu)
      const item = target.closest(`${s.item}`) as HTMLElement
      const c =
        currentFlyout.current == null
          ? void 0
          : currentFlyout.current.closest(`${s.item}`)

      item !== c && e.preventDefault()
      if (open) {
        closeFlyout(currentFlyout.current)
      } else {
        openFlyout(itemSubmenu)
      }
    } else {
      const item = target.closest(`.${s.item}`) as HTMLElement
      if (item == currentFlyout.current) {
        closeFlyout(currentFlyout.current)
        return
      }

      item &&
        item !== currentFlyout.current &&
        item !== menuFlyout.current &&
        (subMenuEls.current?.includes(item)
          ? openFlyout(item)
          : closeFlyout(currentFlyout.current))
    }
  }

  const onSearchMenuClick = (e) => {
    e.preventDefault()
    navigate('/search')
    return
    // if (!isBreakpointWithMenu) {
    //   openSearchPreview()
    //   return
    // }
    // isFlyoutOpen(searchFlyout.current)
    //   ? closeSearch()
    //   : ((shouldDelaySearchOpen.current = navRef.current.classList.contains(
    //       s.animating
    //     )),
    //     !shouldDelaySearchOpen.current && openSearch())
  }
  const openSearch = () => {
    openFlyout(searchFlyout.current)
    navRef.current.classList.add(s.withSearchOpen)
  }
  const closeSearch = () => {
    closeFlyout(searchFlyout.current)
  }

  const onCartMenuClick = (e) => {
    e.preventDefault()
    // if (!isBreakpointWithMenu) {
    navigate('/cart')

    // navRef.current.classList.contains(s.withCartPreviewOpen)
    //   ? closePreviewCart()
    //   : openPreviewCart()
    return
    // }

    // isFlyoutOpen(cartFlyout.current) ? closeCart() : openCart()
  }

  const closeCart = () => {
    closeFlyout(cartFlyout.current)
  }
  const openCart = () => {
    openFlyout(cartFlyout.current)
    navRef.current.classList.add(s.withCartOpen)
  }

  const isFlyoutOpen = (e) => {
    return currentFlyout.current === e
  }

  const initiateFlyouts = () => {
    let navFlyout, flyout, c
    flyouts.current = []
    const flyoutContainer = [
      ...navRef.current.querySelectorAll(`.${s.flyout}`),
    ].map((a) => a.parentNode)
    for (const item of flyoutContainer) {
      let i: NavFlyout = {}
      i.item = item
      i.flyout =
        (navFlyout = i.item) == null
          ? void 0
          : navFlyout.querySelector(`.${s.flyout}`)
      i.scrollContainer =
        (flyout = i.flyout) == null
          ? void 0
          : flyout.querySelector(`:scope > .${s.flyoutScrollContainer}`)
      i.content =
        (c = i.scrollContainer) == null
          ? void 0
          : c.querySelector(`:scope > .${s.submenuContent}`)
      flyouts.current.push(i)
    }
    setFlyoutItems()
    setFlyoutHeights()
    intiaiteSubMenus()
  }
  const setFlyoutItems = () => {
    const setCssVariables = (e: HTMLElement, t = false) => {
      const flyoutItems = e.querySelectorAll(`.${s.flyoutItem}`)
      if (!flyoutItems.length) return
      flyoutItems.forEach((el: HTMLElement, index) => {
        el.style.setProperty('--flyout-item-number', `${index}`)
      })
      e.style.setProperty('--flyout-item-total', `${flyoutItems.length}`)
    }

    flyouts.current.forEach((flyout) => {
      const { flyout: e } = flyout
      setCssVariables(e)
    })
  }
  const setItemNumbers = () => {
    let e = 0

    menuList.current
      .querySelectorAll(`${s.itemMenu}:not(.${s.search}, .${s.hidden})`)
      .forEach((t: HTMLElement, s) => {
        t.style.setProperty('--flyout-item-number', `${s}`), ++e
      })
    menuList.current.style.setProperty('--flyout-item-total', `${e}`)
  }

  const intiaiteSubMenus = () => {
    subMenus.current = flyouts.current
      .filter((e) => (e.item as HTMLElement).classList.contains(s.itemSubmenu))
      .map((x) => {
        const item = x.item as HTMLElement
        const content = item.querySelector(`${s.submenuContent}`)
        return { content } as Submenu
      })
    flyouts.current.filter((e) => {
      return (e.item as HTMLElement).classList.contains(s.itemSubmenu)
    })

    subMenuEls.current = [
      ...navRef.current.querySelectorAll(`.${s.itemSubmenu}`),
    ] as HTMLElement[]
  }
  const setFlyoutHeights = () => {
    let flyoutArr: NavFlyout[]
    ;(flyoutArr = flyouts.current) == null ||
      flyoutArr.forEach((flyout) => setFlyoutHeight(flyout.item as HTMLElement))
  }
  const setFlyoutHeight = (e: HTMLElement, t = 0) => {
    const getFlyoutDuration = (n) =>
      `${Math.min(350, Math.max(240, parseInt(n) / 2))}ms`

    if (isBreakpointWithMenu) {
      const content = contentRef.current,
        a = window.innerHeight,
        duration = getFlyoutDuration(a)
      content.style.setProperty('--flyout-rate', duration)
    } else {
      const item = getFlyoutItem(e)
      if (item?.content) {
        const contentHeight = parseInt(getComputedStyle(item.content).height)
        const navHeight = parseInt(getComputedStyle(navRef.current).height)
        item.flyout.style.setProperty(
          '--flyout-height',
          `${contentHeight + navHeight + t - 50}px`,
        )
        item.flyout.style.setProperty(
          '--flyout-rate',
          getFlyoutDuration(contentHeight + t),
        )
      }
    }
  }

  const openPreviewCart = () => {
    if (disableCartPreview) return
    if (navRef.current.classList.contains(s.withSearchPreviewOpen)) {
      closeSearchPreview()
    }
    flyoutAbortController.current = new AbortController()
    flyoutAbortControllerSignal.current = {
      signal: flyoutAbortController.current?.signal,
    }

    document.addEventListener('scroll', () => closePreviewCart(), {
      signal: flyoutAbortController.current?.signal,
    })
    navRef.current.classList.add(s.withCartPreviewOpen)
    cartPreview.current?.addEventListener(
      'mouseleave',
      () => {
        closePreviewCart()
      },
      { once: true },
    )
  }
  const closePreviewCart = () => {
    flyoutAbortController.current?.abort()
    flyoutAbortController.current = null
    if (navRef.current) navRef.current.classList.remove(s.withCartPreviewOpen)
  }

  const openSearchPreview = () => {
    navRef.current.classList.add(s.withSearchPreviewOpen)
    if (navRef.current.classList.contains(s.withCartPreviewOpen)) {
      closePreviewCart()
    }
    flyoutAbortController.current = new AbortController()
    flyoutAbortControllerSignal.current = {
      signal: flyoutAbortController.current?.signal,
    }

    document.addEventListener('scroll', () => closeSearchPreview(), {
      signal: flyoutAbortController.current?.signal,
    })
    searchPreview.current.addEventListener(
      'mouseleave',
      () => {
        closeSearchPreview()
      },
      { once: true },
    )
  }

  const closeSearchPreview = () => {
    flyoutAbortController.current?.abort()
    flyoutAbortController.current = null
    navRef.current?.classList?.remove(s.withSearchPreviewOpen)
  }

  useEffect(() => {
    initiateFlyouts()
    setItemNumbers()
    const destory = () => {
      closeSearchPreview()
      closePreviewCart()
      closeMenu()
      closeFlyout()
      flyoutAbortController.current?.abort()
    }

    router.events.on('routeChangeStart', destory)

    return () => {
      destory()
      router.events.off('routeChangeStart', destory)
    }
  }, [])

  useEffect(() => {
    if (disableCartPreview) {
      return
    }

    if (totalQuantity > 0) {
      !isBreakpointWithMenu && openPreviewCart()
    }
  }, [totalQuantity])

  useEffect(() => {
    if (headerSpace && navRef.current && backgroundRef.current) {
      navRef.current.classList.remove(s.transparent)
      // backgroundRef.current.classList.add('shadow-magical')
    } else {
      navRef.current.classList.add(s.transparent)
      backgroundRef.current.classList.remove('shadow-magical')
    }
  }, [headerSpace, navRef.current, backgroundRef.current])

  useEffect(() => {
    if (rootWrapper.current) {
      const calloutHeight = parseInt(
        getComputedStyle(calloutRoot.current).height,
      )
      rootWrapper.current.parentElement.style.setProperty(
        '--callout-height',
        `${calloutHeight}px`,
      )
      // if (headerSpaceRef.current)
      //   headerSpaceRef.current.style.setProperty(
      //     '--callout-height',
      //     `${calloutHeight}px`
      //   )
    }
  }, [rootWrapper.current, headerSpaceRef.current])

  useEffect(() => {
    setFlyoutHeights()
    closeMenu()
    closeFlyout()
    fixedClearAllBodyScrollLocks()
    flyoutAbortController.current?.abort()

    //resize observer
    const resizeObserver = new ResizeObserver((entries) => {
      setFlyoutHeights()
    })
    resizeObserver.observe(navRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [isBreakpointWithMenu])

  useEffect(() => {
    if (flyoutSearchInput.current) {
      initiateFlyouts()
    }
  }, [flyoutSearchInput.current])

  return (
    <>
      <div className={s.rootWrapper} ref={rootWrapper} data-elastic-exclude>
        <aside className={s.calloutRoot} ref={calloutRoot}>
          <CalloutBanner />
        </aside>
        <nav
          id="navbar-root"
          className={clsx(s.root, s.transparent, s.withCartCount, {
            [s.withoutCart]: !ecommerceEnabled,
          })}
          ref={navRef}
        >
          <div
            id="navbar-background"
            className={clsx(s.background)}
            ref={backgroundRef}
          />
          <div className={clsx(s.content)} ref={contentRef}>
            <div className={clsx(s.menuTrigger, s.link)}>
              <button
                className={s.menuTriggerButton}
                onClick={onMenuButtonClick}
                ref={menuButton}
              >
                <span className={clsx(s.menuTriggerIcon)}>
                  <Hamburger />
                </span>
              </button>
            </div>
            <div className={clsx(s.item, s.menuback)}>
              <button
                className={s.menubackButton}
                onClick={onMenuBackButtonClick}
              >
                <span className={clsx(s.chevronIcon)}>
                  <svg
                    height="48"
                    viewBox="0 0 9 48"
                    width="9"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="m1.5618 24.0621 6.5581-6.4238c.2368-.2319.2407-.6118.0088-.8486-.2324-.2373-.6123-.2407-.8486-.0088l-7 6.8569c-.1157.1138-.1807.2695-.1802.4316.001.1621.0674.3174.1846.4297l7 6.7241c.1162.1118.2661.1675.4155.1675.1577 0 .3149-.062.4326-.1846.2295-.2388.2222-.6187-.0171-.8481z"></path>
                  </svg>
                </span>
                <span className={s.menuBackChevronText}>Back</span>
              </button>
            </div>

            <ul
              className={s.list}
              onMouseOver={(e) =>
                !isBreakpointWithMenu && onMenuItemMouseOver(e)
              }
            >
              <li className={clsx(s.menu, s.item)} ref={menuFlyout}>
                <div className={s.flyout}>
                  <div className={s.menuList} ref={menuList}>
                    <div className={clsx(s.item, s.itemMenu)}>
                      <div className={s.submenuTriggerGroup}>
                        <div className={s.submenuTriggerItem}>
                          <ul className={s.submenuTriggerItemList}>
                            <li className={s.submenuTriggerItemListItem}>
                              <Link
                                link={{ url: retailerUrl }}
                                className={s.menuTriggerButton}
                              >
                                <Location />
                              </Link>
                            </li>
                            <li className={s.submenuTriggerItemListItem}>
                              <Link
                                link={{ url: '/newsletter' }}
                                className={s.menuTriggerButton}
                              >
                                <div className={s.newsletterWrapper}>
                                  <svg
                                    className={s.newsletterIcon}
                                    version="1.1"
                                    id="Layer_1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                    x="0px"
                                    y="0px"
                                    viewBox="0 0 799.7 550.4"
                                    xmlSpace="preserve"
                                  >
                                    <rect
                                      x="59.9"
                                      y="59.3"
                                      className="st0"
                                      width="683.1"
                                      height="430.8"
                                    />
                                    <polyline
                                      className="st0"
                                      points="59.9,99.9 401.4,317.7 742.9,99.9 "
                                    />
                                  </svg>
                                </div>
                              </Link>
                            </li>
                            {ecommerceEnabled && (
                              <li className={s.submenuTriggerItemListItem}>
                                <div className={s.wishlistWrapper}>
                                  <Link
                                    link={{ url: wishlistUrl ?? '' }}
                                    className={s.menuTriggerButton}
                                  >
                                    <Heart
                                      width={28}
                                      height={23}
                                      color="white"
                                    />
                                  </Link>
                                </div>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                    {headerDataV2 &&
                      headerDataV2.nav.map(({ content, settings }, index) => {
                        invariant(
                          content.__typename === 'HeaderLinkV2' ||
                            content.__typename === 'HeaderDropdown',
                          'Invalid content type',
                        )
                        invariant(
                          settings.__typename === 'HeaderV2ItemSettings',
                          'Invalid settings type',
                        )
                        return (
                          <NavItem
                            onMenuItemButtonClick={onMenuItemButtonClick}
                            key={index}
                            {...content}
                            className={clsx(
                              s.item,
                              s.itemMenu,
                              {
                                [s.desktopHidden]: !settings?.visibleOnDesktop,
                              },
                              { [s.mobileHidden]: !settings?.visibleOnMobile },
                            )}
                          />
                        )
                      })}
                  </div>
                </div>
              </li>
              <li className={clsx(s.logo, s.item)}>
                <Link link={{ url: '/' }} className={s.link}>
                  <span className={s.linkLogo}>
                    <GubiLogo special={useSpecialLogo} />
                  </span>
                </Link>
              </li>
              <li className={clsx(s.item, s.search)} ref={searchFlyout}>
                {/* <SearchProvider config={DEFAULT_ELASTIC_SEARCH_CONFIG}> */}
                <Link
                  link={{ url: '/search' }}
                  className={clsx(s.searchFieldForm, s.link)}
                >
                  {/* <div
                    className={clsx(s.searchFieldWrapper)}
                    onClick={(e) => {
                      onSearchMenuClick(e)
                    }}
                    // onMouseEnter={() => {
                    //   !isBreakpointWithMenu && openSearchPreview()
                    // }}
                  >
                    <span className={s.searchPlaceholder}>Search</span>

                    <button
                      className={s.searchFieldSubmit}
                      type="button"
                      disabled
                    >
                      <span className={s.searchIcon}>
                        <Search />
                      </span>
                    </button>
                  </div> */}
                  <Search />
                </Link>
                {/* SEARCH PREVIEW */}
                {/* <SearchPreview
                    closeSearchPreview={closeSearchPreview}
                    ref={searchPreview}
                  /> */}
                {/* SEARCH PREVIEW END */}
                {/* <div className={clsx(s.submenu, s.flyout)}>
                    <div className={s.flyoutScrollContainer}>
                      <div className={clsx(s.flyoutContent, s.submenuContent)}>
                        <SearchBox
                          autocompleteMinimumCharacters={3}
                          searchAsYouType
                          shouldClearFilters={false}
                          debounceLength={300}
                          view={({ onSubmit, onChange, value }) => (
                            <form
                              className={clsx(
                                s.flyoutSearchField,
                                s.flyoutItem
                              )}
                              onSubmit={onSubmit}
                            >
                              <div
                                className={clsx(s.flyoutSearchFieldWrapper)}
                                // onClick={(e) => {
                                //   onSearchMenuClick(e)
                                // }}
                              >
                                <input
                                  placeholder="Search"
                                  type="text"
                                  value={value}
                                  autoCorrect="off"
                                  autoComplete="off"
                                  autoCapitalize="off"
                                  className={s.flyoutSearchFieldInput}
                                  onChange={(e) => {
                                    onChange(e.currentTarget.value)
                                  }}
                                  ref={flyoutSearchInput}
                                />
                                <button
                                  className={s.flyoutSearchFieldSubmit}
                                  type="button"
                                  disabled
                                >
                                  <span className={s.searchIcon}>
                                    <Search />
                                  </span>
                                </button>
                              </div>
                            </form>
                          )}
                        />
                        <div className={clsx(s.resultWrapper, s.flyoutItem)}>
                          <Results
                            //@ts-ignore
                            onResultChange={() =>
                              setFlyoutHeight(searchFlyout.current)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div> */}
                {/* </SearchProvider> */}
              </li>
              <li className={clsx(s.item, s.mobileHidden)}>
                <div className={s.locationWrapper}>
                  <Link link={{ url: retailerUrl }} className={s.link}>
                    <Location />
                  </Link>
                </div>
              </li>

              <li className={clsx(s.item)}>
                <Link
                  link={{
                    url: `${process.env.NEXT_PUBLIC_BASEURL}/account/login`,
                  }}
                  className={s.link}
                >
                  <Account height={24} width={24} />
                </Link>
              </li>
              {ecommerceEnabled && (
                <>
                  <li className={clsx(s.item, s.mobileHidden)}>
                    <div className={s.wishlistWrapper}>
                      <Link
                        link={{ url: wishlistUrl ?? '' }}
                        className={s.link}
                      >
                        <Heart width={28} height={23} />
                        {wishlistCount > 0 && (
                          <span className={s.wishlistBadge}>
                            <span className={s.badgeCount}>
                              {wishlistCount}
                            </span>
                          </span>
                        )}
                      </Link>
                    </div>
                  </li>

                  <li
                    className={clsx(s.item, s.cart, s.withBadge)}
                    ref={cartFlyout}
                  >
                    <div className={s.cartWrapper}>
                      <Link
                        className={clsx(s.cartButton, s.link)}
                        // onClick={onCartMenuClick}
                        link={{ url: '/cart' }}
                        onMouseEnter={() => {
                          !isBreakpointWithMenu && openPreviewCart()
                        }}
                      >
                        <ShoppingCartIcon />
                      </Link>
                      {totalQuantity > 0 && (
                        <span className={s.cartBadge}>
                          <span className={s.badgeCount}>{totalQuantity}</span>
                        </span>
                      )}
                    </div>
                    {/**
                     * CART PREVIEW POPUP DESKTOP ONLY
                     */}
                    <CartPreview
                      ref={cartPreview}
                      closePreviewCart={closePreviewCart}
                    />
                    {/**
                     * CART FLYOUT MOBILE/TABLET
                     * */}
                    <div className={clsx(s.submenu, s.flyout)}>
                      <div className={s.flyoutScrollContainer}>
                        <div
                          className={clsx(s.flyoutContent, s.submenuContent)}
                        >
                          <Cart />
                        </div>
                      </div>
                    </div>
                  </li>
                </>
              )}
            </ul>
            <div className={clsx(s.menuTrigger, s.menuTriggerClose)}>
              <button className={s.menuTriggerButton} onClick={closeMenu}>
                <span>
                  <Cross width={28} height={28} />
                </span>
              </button>
            </div>
          </div>
        </nav>
      </div>
      {headerSpace && (
        <div
          className={clsx(s.headerSpace, { [s.callout]: calloutBanner })}
          ref={headerSpaceRef}
        />
      )}
    </>
  )
}

export default Navbar
